// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import { BaseMoreAction, BaseTable, BaseToolbar, ConfirmModal, Loading } from '~/components/common';
import { ClientContactForm } from '~/components/module/client';
// Others
import { getDetailClient, updateClient } from '~/thunks/client/clientThunk';
import { createFormData, formatPhoneNumber, formatTitleCase, getFullName } from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IBaseMoreAction, IBodyUpdate, IClientContact, IHeaderCellType } from '~/utils/interface/common';
import { IParamDetailClient, IQueryDetailClient } from '~/utils/interface/client';
import { IBaseResponse, IListDataResponse, IPaginationResponse } from '~/utils/interface/base';
import { TypeDetailClientEnum } from '~/utils/enum/common';
import { DEFAULT_CURRENT_PAGE, DEFAULT_LIMIT_PAGE, EMPTY_STRING } from '~/utils/constants/common';
// Styles, images, icons
import styles from './ContactClientDetailTab.module.scss';
import { icons } from '~/assets';
import { clientActions, selectIsRefreshDetailClient } from '~/thunks/client/clientSlice';
import { useSelector } from 'react-redux';

type Props = {};

const cx = classNames.bind(styles);

const columns = (
  t: TFunction,
  onEdit: (record: IClientContact) => void,
  onDelete: (record: IClientContact) => void
): IHeaderCellType<IClientContact>[] => {
  return [
    {
      id: 'name',
      label: t('contact_title_table_name'),
      render: (record: IClientContact) => (
        <Stack>
          <Typography className={cx('textTitleTable')}>{getFullName({ firstName: record?.firstName })}</Typography>
        </Stack>
      ),
    },
    {
      id: 'email',
      label: t('contact_title_table_email'),
      render: (record: IClientContact) => (
        <Stack>
          <Typography className={cx('textTitleTable')}>{record?.email}</Typography>
        </Stack>
      ),
    },
    {
      id: 'phone',
      label: t('contact_title_table_phone'),
      render: (record: IClientContact) => (
        <Stack>
          <Typography className={cx('textTitleTable')}>{formatPhoneNumber(record?.phoneNumber)}</Typography>
        </Stack>
      ),
    },
    {
      id: 'phone',
      label: t('contact_title_table_relationship'),
      render: (record: IClientContact) => (
        <Stack>
          <Typography className={cx('textTitleTable')}>{formatTitleCase(record?.contactType)}</Typography>
        </Stack>
      ),
    },
    {
      id: 'action',
      label: t('task_table_action_title'),
      minWidth: 100,
      align: 'center',
      render: (record: IClientContact) => {
        const actions: IBaseMoreAction[] = [
          {
            label: t('table_action_edit', { ns: 'common/base' }),
            icon: (
              <img
                src={icons.tableIconActionEdit}
                alt={t('common_alt_img', { ns: 'common/base' })}
                width={16}
                height={16}
              />
            ),
            onClick: () => onEdit(record),
          },
          {
            label: t('table_action_delete', { ns: 'common/base' }),
            icon: (
              <img
                src={icons.tableIconActionDelete}
                alt={t('common_alt_img', { ns: 'common/base' })}
                width={16}
                height={16}
              />
            ),
            hasOtherColor: true,
            onClick: () => onDelete(record),
          },
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const ContactClientDetailTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/client', 'common/base']);
  const dispatch = useAppDispatch();
  const { id: clientId } = useParams();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  //#endregion Declare Hook

  //#region Selector
  const isRefreshDetailClient = useSelector(selectIsRefreshDetailClient);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowFormContact, setIsShowFormContact] = useState<boolean>(false);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);

  const [paramObject, setParamObject] = useState<IQueryDetailClient>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [contactList, setContactList] = useState<IClientContact[]>([]);
  const [contactSelected, setContactSelected] = useState<IClientContact>();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const params: IParamDetailClient = {
      clientId: clientId || EMPTY_STRING,
      type: TypeDetailClientEnum.ADDITIONAL_CONTACT,
      query: paramObject,
    };

    handleGetListContactClient(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshDetailClient) return;

    const params: IParamDetailClient = {
      clientId: clientId || EMPTY_STRING,
      type: TypeDetailClientEnum.ADDITIONAL_CONTACT,
      query: paramObject,
    };

    handleGetListContactClient(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshDetailClient]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListContactClient = (params: IParamDetailClient) => {
    setIsLoading(true);

    dispatch(getDetailClient(params))
      .unwrap()
      .then((res) => {
        const { data } = res as IBaseResponse<IListDataResponse<IClientContact[]>>;

        if (!data) return;

        const { responses, pagination } = data;

        if (!responses) return;

        setContactList(responses);
        setPagination(pagination);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleView = (item: IClientContact) => {
    if (!item) return;

    setContactSelected(item);
    handleShowFormContact();
  };

  const handleEdit = (item: IClientContact) => {
    if (!item) return;

    setContactSelected(item);
    handleShowFormContact();
  };

  const handleDelete = (item: IClientContact) => {
    if (!item) return;

    setContactSelected(item);
    setIsShowConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    setIsLoading(true);

    const formatData = {
      deletedAdditionalContactIds: [contactSelected?.id],
    };

    const formData = await createFormData(formatData);

    const params: IBodyUpdate<FormData> = {
      id: clientId || EMPTY_STRING,
      body: formData,
    };

    dispatch(updateClient(params))
      .unwrap()
      .then(() => {
        dispatch(clientActions.setRefreshClientDetail(true));
        handleCloseConfirmDelete();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleShowFormContact = () => {
    setIsShowFormContact(true);
  };

  const handleCloseFormContact = () => {
    setIsShowFormContact(false);
    setContactSelected(undefined);
  };

  const handleCloseConfirmDelete = () => {
    setContactSelected(undefined);
    setIsShowConfirmDelete(false);
  };

  //#endregion Handle Function

  return (
    <Stack id='contactClientDetailTabComponent' className={cx('container')}>
      <Stack className={cx('viewHeader')}>
        <BaseToolbar title={t('contact_title')} description={t('contact_description')}>
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='contained'
              className={cx('buttonAction')}
              onClick={handleShowFormContact}
              startIcon={<img src={icons.commonIconPlus} alt={'common_alt_img'} />}
            >
              {t('contact_add_contact_button')}
            </Button>
          </Box>
        </BaseToolbar>
      </Stack>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          dataSource={contactList || []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columns(t, handleEdit, handleDelete)}
          onPageChange={handlePageChange}
          onRowClick={(_id, record) => handleEdit(record)}
        />
      </Box>

      {isShowFormContact && (
        <ClientContactForm listContact={contactList} dataDefault={contactSelected} onClose={handleCloseFormContact} />
      )}

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isLoading && <Loading />}
    </Stack>
  );
};

export default ContactClientDetailTab;
